import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import sortBy from 'lodash/sortBy';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { roundValue } from '../../../utils/math';
import { queryHashGenerator } from '../../../utils/queryHash';
import { zScore } from '../../../utils/scoring';
import { testMetaString } from '../../../utils/testMetaString';
import {
  Entity,
  EntityType,
  IAggregateWithMetric,
  IAthlete,
  IMetric,
  ISelectableEntity,
  ISelectableTag,
  ITag,
  MetricDirection,
} from '../../types';
import { IAggregates } from '../aggregatesProvider';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import { entitiesProvider, IEntities } from '../entitiesProvider';
import { IComparisonPopulationSetting } from '../settings/comparisonPopulationSettingsProvider';
import { IDateRange } from '../settings/dates/dateRangeProvider';
import {
  IQuadrantReportSettings,
  IQuadrantReportSettingsState,
  quadrantReportSettingsProvider,
} from '../settings/reports/quadrantReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import { determinePopulation } from '../utils/determinePopulation';
import {
  convertTimeframeToLocalTime,
  formatTimeframe,
} from '../utils/formatTimeframe';
import { getSessionRangeTimestamps } from '../utils/getSessionRangeTimestamps';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportState, IValidationResult } from './types';

export interface IQuadrantPoint {
  timeframe: string;
  value: number;
  units: string;
  roundingPrecision: number;
  zScore: number;
  populationCount: number;
  count: number;
  metricLabel: string;
  testMeta: string;
  metricDirection: MetricDirection;
}

export interface IQuadrantDefinition {
  label: string;
  xDescription: string;
  yDescription: string;
}

export interface IQuadrantResult {
  entity: Entity;
  x: IQuadrantPoint;
  y: IQuadrantPoint;
  rankingScore: number;
  quadrantDefinition: IQuadrantDefinition;
  rank?: number;
}

interface IPopulationAxisResult {
  mean: number;
  roundingPrecision: number;
  units: string;
  metricLabel: string;
  testMeta: string;
}

export interface IQuadrantReportState extends IReportState {
  timeframe: string;
  targetTimeframe: IDateRange;
  settings: ISettingsTemplate<IQuadrantReportSettings>;
  comparisonPopulation: IComparisonPopulationSetting;
  quadrantResults: IQuadrantResult[];
  xAxisTestMeta: string;
  xAxisMetricLabel: string;
  xAxisDirection: MetricDirection;
  yAxisTestMeta: string;
  yAxisMetricLabel: string;
  yAxisDirection: MetricDirection;
  populationResult: {
    x: IPopulationAxisResult;
    y: IPopulationAxisResult;
  };
}

interface IEntityQueryArg {
  id: string;
  type: EntityType;
  entity: Entity;
}

interface ITargetData {
  entity: Entity;
  data: ICrossTestAggregates;
}

interface IAxisDirections {
  xAxisDirection: MetricDirection;
  yAxisDirection: MetricDirection;
}

interface IEntitiesStageData extends IQuadrantReportSettingsState {
  entityQueries: IEntityQueryArg[];
}
interface ITargetStageData extends IQuadrantReportSettingsState {
  targetData: ITargetData[];
  hashes: string[];
}

interface IPopulationStageData extends ITargetStageData {
  populationData: ICrossTestAggregates;
}

export const quadrantReportProvider = (
  entityType: EntityType,
  entity: Entity
): Observable<IQuadrantReportState> => {
  return quadrantReportSettingsProvider().pipe(
    /// this stage gets the individual athletes if selected entity is a group or a team
    switchMap(
      (
        settings: IQuadrantReportSettingsState
      ): Observable<IEntitiesStageData> => {
        if (entityType === 'groups' || entityType === 'teams') {
          return entitiesProvider().pipe(
            map((entities: IEntities): IEntitiesStageData => {
              /// filter the athletes that are members of the group or team
              const athletes = entities.athletes.filter(
                (a: IAthlete): boolean => a[entityType].includes(entity.id)
              );
              return {
                ...settings,
                entityQueries: athletes.map(
                  (mEntity: IAthlete): IEntityQueryArg => ({
                    id: mEntity.id,
                    type: 'athletes',
                    entity: mEntity,
                  })
                ),
              };
            })
          );
        } else {
          ///  the selected entity is an athlete
          return of({
            ...settings,
            entityQueries: [{ id: entity.id, type: entityType, entity }],
          });
        }
      }
    ),
    /// this stage fetches the target data for the entities
    switchMap(
      (
        settingsAndEntities: IEntitiesStageData
      ): Observable<ITargetStageData> => {
        const settingsValidation = validateSettings(settingsAndEntities);

        if (settingsValidation.isValid) {
          if (settingsAndEntities.entityQueries.length > 0) {
            const { xHash, yHash } = makeHashes(
              settingsAndEntities.selected?.settings
            );
            const hashes = xHash === yHash ? [xHash] : [xHash, yHash];
            return combineLatest(
              settingsAndEntities.entityQueries.map(
                (qe: IEntityQueryArg): Observable<ITargetData> => {
                  return crossTestAggregates(
                    hashes ?? [],
                    qe.id,
                    qe.type as EntityType,
                    settingsAndEntities.timeframe?.dateType,
                    settingsAndEntities.timeframe.dateRange
                  ).pipe(
                    map(
                      (data: ICrossTestAggregates): ITargetData => ({
                        entity: qe.entity,
                        data,
                      })
                    )
                  );
                }
              )
            ).pipe(
              map((targetData: ITargetData[]): ITargetStageData => {
                return {
                  targetData,
                  ...settingsAndEntities,
                  hashes,
                };
              })
            );
          } else {
            const targetStageData: ITargetStageData = {
              ...settingsAndEntities,
              hashes: null,
              targetData: [],
            };
            return of(targetStageData);
          }
        } else {
          return of({
            ...settingsAndEntities,
            targetData: [],
            hashes: null,
          });
        }
      }
    ),
    /// this stage fetches the population data
    switchMap((data: ITargetStageData): Observable<IPopulationStageData> => {
      if (isEmpty(data?.comparisonPopulation)) {
        return of({
          ...data,
          populationData: null,
        });
      }
      const [populationEntityType, populationEntityId] = determinePopulation(
        data?.comparisonPopulation?.entity,
        {
          entityId: entity.id,
          entityType,
        }
      );

      return crossTestAggregates(
        data.hashes ?? [],
        populationEntityId,
        populationEntityType as EntityType,
        data.comparisonPopulation?.dateRangeType,
        data?.comparisonPopulation?.dateRangeValues
      ).pipe(
        map((populationData: ICrossTestAggregates): IPopulationStageData => {
          return {
            ...data,
            populationData,
          };
        })
      );
    }),
    /// this stage creates the finalized report data
    map((results: IPopulationStageData): IQuadrantReportState => {
      const timeframe =
        results.timeframe?.dateType?.value === 'custom'
          ? formatTimeframe(
              results.timeframe?.dateRange?.from?.value,
              results.timeframe?.dateRange?.to?.value
            )
          : results.timeframe?.dateType?.label;
      const quadrantResults = makeQuadrantResults(
        results.targetData,
        results.populationData,
        results.selected?.settings
      );

      const populationResult = makePopulationResult(
        results?.selected?.settings,
        results.populationData
      );

      const { xAxisTestMeta, xAxisMetricLabel } = makeAxisLabels(
        'xAxis',
        results?.selected?.settings?.xAxis
      );

      const { yAxisTestMeta, yAxisMetricLabel } = makeAxisLabels(
        'yAxis',
        results?.selected?.settings?.yAxis
      );

      const axisDirections = determineAxisDirections(quadrantResults);

      /// validate settings
      const settingsValidation = validateSettings(results);
      if (!settingsValidation.isValid) {
        return {
          ...settingsValidation,
          targetTimeframe: results.timeframe,
          settings: results.selected,
          comparisonPopulation: results.comparisonPopulation,
          quadrantResults,
          populationResult,
          yAxisMetricLabel,
          yAxisTestMeta,
          xAxisTestMeta,
          xAxisMetricLabel,
          timeframe,
          ...axisDirections,
        };
      }

      /// validate data
      const dataValidation = validateData(quadrantResults);

      const localComparisonTimeframe = convertTimeframeToLocalTime({
        dateType: results.comparisonPopulation?.dateRangeType,
        dateRange: results.comparisonPopulation?.dateRangeValues,
      });

      return {
        settings: results.selected,
        targetTimeframe: convertTimeframeToLocalTime(results.timeframe),
        comparisonPopulation: {
          entity: results.comparisonPopulation.entity,
          dateRangeType: localComparisonTimeframe.dateType,
          dateRangeValues: localComparisonTimeframe.dateRange,
        },
        quadrantResults,
        populationResult,
        yAxisMetricLabel,
        yAxisTestMeta,
        xAxisTestMeta,
        xAxisMetricLabel,
        timeframe,
        ...axisDirections,
        ...dataValidation,
      };
    })
  );
};

const validateData = (results: IQuadrantResult[]): IValidationResult => {
  if (results.length > 0) {
    return {
      isValid: true,
    };
  } else {
    return {
      isValid: false,
      errorCode: 'insufficient-data',
    };
  }
};

/**
 * @description finds the data used in axis labels
 * @param axis
 * @param settings
 * @returns
 */
const makeAxisLabels = (
  axis: 'xAxis' | 'yAxis',
  settings: IQuadrantReportSettings['xAxis'] | IQuadrantReportSettings['yAxis']
): { [key: string]: string } => {
  const testMeta = testMetaString(
    settings?.testType?.entity.abbr,
    settings?.tags?.map((st: ISelectableTag): ITag => st.entity)
  );
  const metricLabel = settings?.metric.label;
  return {
    [`${axis}TestMeta`]: testMeta,
    [`${axis}MetricLabel`]: metricLabel,
  };
};

/**
 * @description determines the direction of each axis
 * @param results
 * @returns
 */
const determineAxisDirections = (
  results: IQuadrantResult[]
): IAxisDirections => {
  const first = results.length > 0 ? results[0] : null;
  if (!first) {
    return {
      xAxisDirection: null,
      yAxisDirection: null,
    };
  }
  return {
    xAxisDirection: first.x.metricDirection,
    yAxisDirection: first.y.metricDirection,
  };
};

/**
 * @description makes population axis data
 * @param settings
 * @param data
 * @returns
 */
const makePopulationResult = (
  settings: IQuadrantReportSettings,
  data: ICrossTestAggregates
): { x: IPopulationAxisResult; y: IPopulationAxisResult } => {
  const { xHash, yHash } = makeHashes(settings);
  if (isEmpty(data)) return null;

  return {
    x: {
      mean: data[xHash]?.combined?.data?.[settings.xAxis?.metric?.value].mean,
      roundingPrecision:
        data[xHash]?.combined?.data?.[settings.xAxis?.metric?.value]
          ?.roundingPrecision,
      units:
        data[xHash]?.combined?.data?.[settings.xAxis?.metric?.value]?.units,
      metricLabel: settings.xAxis?.metric?.label,
      testMeta: testMetaString(
        settings.xAxis?.testType?.entity.abbr,
        settings.xAxis?.tags?.map((st: ISelectableTag): ITag => st.entity)
      ),
    },
    y: {
      mean: data[yHash]?.combined?.data?.[settings.yAxis?.metric?.value]?.mean,
      roundingPrecision:
        data[yHash]?.combined?.data?.[settings.yAxis?.metric?.value]
          ?.roundingPrecision,

      units:
        data[yHash]?.combined?.data?.[settings.yAxis?.metric?.value]?.units,
      metricLabel: settings.yAxis?.metric?.label,
      testMeta: testMetaString(
        settings.yAxis?.testType?.entity.abbr,
        settings.yAxis?.tags?.map((st: ISelectableTag): ITag => st.entity)
      ),
    },
  };
};

/**
 * @description creates the quadrant results
 * @param results
 * @param populationData
 * @param reportSettings
 * @returns quadrant results
 */
const makeQuadrantResults = (
  results: ITargetData[],
  populationData: ICrossTestAggregates,
  reportSettings: IQuadrantReportSettings
): IQuadrantResult[] => {
  const xTestMeta = testMetaString(
    reportSettings?.xAxis?.testType?.entity.abbr,
    reportSettings?.xAxis?.tags?.map((se: ISelectableTag): ITag => se.entity)
  );

  const yTestMeta = testMetaString(
    reportSettings?.yAxis?.testType?.entity.abbr,
    reportSettings?.yAxis?.tags?.map((se: ISelectableTag): ITag => se.entity)
  );
  return sortBy(
    results
      .map((curr: ITargetData): IQuadrantResult => {
        const { xHash, yHash } = makeHashes(reportSettings);
        const xMetric = reportSettings?.xAxis?.metric.entity;
        const yMetric = reportSettings?.yAxis?.metric.entity;

        /// create x point
        const xPointData = makePointData(
          xTestMeta,
          xMetric,
          curr.data[xHash],
          populationData[xHash]
        );

        /// create y point
        const yPointData = makePointData(
          yTestMeta,
          yMetric,
          curr.data[yHash],
          populationData[yHash]
        );

        const quadrantDefinition = makeQuadrantDefinition(
          xPointData,
          yPointData
        );

        return {
          entity: curr.entity,
          x: xPointData,
          y: yPointData,
          rankingScore: (xPointData.zScore + yPointData.zScore) / 2,
          quadrantDefinition,
        };
      })
      /// remove invalid results
      .filter(
        (result: IQuadrantResult): boolean =>
          result.x.count > 0 && result.y.count > 0
      ),
    (result: IQuadrantResult): number => {
      return isNumber(result.rankingScore) ? -result.rankingScore : Infinity;
    }
  ).map(
    /// add rank
    (result: IQuadrantResult, index: number): IQuadrantResult => ({
      ...result,
      rank: index + 1,
    })
  );
};

/**
 * @description determines the quadrant the athlete falls in
 * @param xAxis
 * @param yAxis
 * @returns
 */
const makeQuadrantDefinition = (
  xAxisPoint: IQuadrantPoint,
  yAxisPoint: IQuadrantPoint
): IQuadrantDefinition => {
  if (aboveAverage(xAxisPoint)) {
    if (aboveAverage(yAxisPoint)) {
      return {
        label: 'Quadrant I',
        xDescription: `Above Average ${xAxisPoint.metricLabel}`,
        yDescription: `Above Average ${yAxisPoint.metricLabel}`,
      };
    } else {
      return {
        label: 'Quadrant IV',
        xDescription: `Above Average ${xAxisPoint.metricLabel}`,
        yDescription: `Below Average ${yAxisPoint.metricLabel}`,
      };
    }
  } else {
    if (aboveAverage(yAxisPoint)) {
      return {
        label: 'Quadrant II',
        xDescription: `Below Average ${xAxisPoint.metricLabel}`,
        yDescription: `Above Average ${yAxisPoint.metricLabel}`,
      };
    } else {
      return {
        label: 'Quadrant III',
        xDescription: `Below Average ${xAxisPoint.metricLabel}`,
        yDescription: `Below Average ${yAxisPoint.metricLabel}`,
      };
    }
  }
};

const aboveAverage = (pointData: IQuadrantPoint): boolean => {
  return pointData.zScore > 0;
};

/**
 * @description creates x/y axis point data
 * @param metricKey
 * @param data
 * @param populationData
 * @returns
 */
const makePointData = (
  testMeta: string,
  metric: IMetric,
  data: IAggregates,
  populationData: IAggregates
): IQuadrantPoint => {
  const target = data?.combined?.data?.[metric.id] as IAggregateWithMetric;
  const population = populationData.combined?.data?.[
    metric.id
  ] as IAggregateWithMetric;
  if (target && population) {
    const targetMean = target?.mean ?? 0;
    const populationMean = population?.mean ?? 0;

    const { start, end } = getSessionRangeTimestamps(data.sessions);

    return {
      value: roundValue(targetMean, target.roundingPrecision),
      units: target.units,
      roundingPrecision: target.roundingPrecision,
      count: target.count,
      zScore: zScore(
        target?.mean,
        population?.mean,
        population.populationSd,
        target.direction
      ),

      populationCount: population.count,
      timeframe: formatTimeframe(start, end),
      metricLabel: metric.label,
      testMeta,
      metricDirection: target.direction,
    };
  } else {
    return {
      value: null,
      units: null,
      roundingPrecision: null,
      count: 0,
      populationCount: 0,
      zScore: null,
      timeframe: null,
      metricLabel: metric.label,
      testMeta,
      metricDirection: null,
    };
  }
};

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: IQuadrantReportSettingsState
): IValidationResult => {
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }
  /// check to make xAxis is defined

  if (!settings?.selected?.settings?.xAxis) {
    return {
      isValid: false,
      errorCode: 'missing-axis-settings',
    };
  }
  /// check to make yAxis is defined
  if (!settings?.selected?.settings?.yAxis) {
    return {
      isValid: false,
      errorCode: 'missing-axis-settings',
    };
  }
  /// check to make sure data set A is valid
  if (
    !isValidDateRange({
      dateType: settings?.timeframe?.dateType,
      dateRange: settings?.timeframe?.dateRange,
    })
  ) {
    return {
      isValid: false,
      errorCode: 'invalid-date-range',
    };
  }

  return { isValid: true };
};

const makeHashes = (
  settings: IQuadrantReportSettings
): { xHash: string; yHash: string } => {
  const xHash = queryHashGenerator(
    settings?.xAxis?.testType?.value,
    settings?.xAxis?.tags?.map((se: ISelectableEntity): string => se.value)
  );
  const yHash = queryHashGenerator(
    settings?.yAxis?.testType?.value,
    settings?.yAxis?.tags?.map((se: ISelectableEntity): string => se.value)
  );
  return { xHash, yHash };
};
